import Draggable from 'react-draggable';
import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ColorPicker,
  type ColorPickerChangePayload,
} from '@/components/inspector-fields/ColorPicker/ColorPicker';
import ColorHelper from 'color';
import clsx from 'clsx';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import IconMoreVertical from '@/assets/icons/icon-more-vertical.svg?react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import type { Color, Gradient, RGBA } from '@builder4/shared/types/layout';
import {
  checkIsGradient,
  getGradientString,
  rgbaToHexa,
} from '@builder4/shared/utils/styleHelpers';

interface ColorStyleProps {
  className: string;
  onClick?: () => void;
  label?: string;
  value: RGBA | Gradient;
  onRename?: (value: string) => void;
  onChange?: (value: Color) => void;
}

export const ColorStyle: React.FC<ColorStyleProps> = ({
  className = '',
  onClick,
  label = 'Color',
  value,
  onRename,
  onChange,
}) => {
  // Unique instance id used to coordinate exclusive open state across multiple Color pickers
  const instanceId = useId();
  const [initialColor, setInitialColor] = useState<RGBA | Gradient>(value);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerPos, setPickerPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [pickerDetached, setPickerDetached] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [name, setName] = useState(label || 'Color');

  const swatchRef = useRef<HTMLDivElement | null>(null);
  const draggableRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const renameContainerRef = useRef<HTMLDivElement | null>(null);
  const isRenamingRef = useRef<boolean>(isRenaming);

  const isGradient = checkIsGradient(initialColor);
  const isSolid = !isGradient;

  useEffect(() => {
    if (value) {
      setInitialColor(value);
    }
  }, [value]);

  const openPicker = () => {
    // Notify other instances to close before opening this one
    try {
      window.dispatchEvent(new CustomEvent('lp:colorpicker-open', { detail: { id: instanceId } }));
      // mark global open so dropdowns don't auto-close while interacting with the picker
      (window as unknown as { __lpColorPickerOpen?: number }).__lpColorPickerOpen =
        ((window as unknown as { __lpColorPickerOpen?: number }).__lpColorPickerOpen ?? 0) + 1;
    } catch {
      /* noop */
    }

    const rect = swatchRef.current?.getBoundingClientRect();
    setPickerDetached(false);
    if (rect) {
      const gap = 16;
      const approxW = 320; // estimated picker width for initial placement
      const approxH = 380; // estimated picker height for initial placement
      // Vertically center to the swatch
      const midY = rect.top + rect.height / 2;
      const top = Math.max(8, Math.min(midY - approxH / 2, window.innerHeight - approxH - 8));
      // Prefer showing to the right of the swatch; clamp if near viewport edge
      const rightPref = rect.right + gap;
      const left = Math.min(rightPref, window.innerWidth - approxW - 8);
      setPickerPos({ top, left });
      setShowPicker(true);
    } else {
      setShowPicker(true);
    }
  };

  const handleClick = () => {
    if (onClick) onClick();
  };

  const handleRenameSelect = () => {
    setIsRenaming(true);
  };

  const finishRename = (commit: boolean) => {
    setIsRenaming(false);
    if (!commit) return;
    const next = (name || '').trim();
    if (!next || next === label) return;
    onRename?.(next);
  };

  // Map ColorPicker's value to this field's value/handlers
  const getSolidColorFromPayload = (payload: ColorPickerChangePayload) => {
    let color = ColorHelper('#000000');
    if (payload.mode === 'solid') {
      color = ColorHelper(`#${payload.hex}`).alpha(Math.max(0, Math.min(1, payload.opacity / 100)));
    } else if (payload.mode === 'gradient') {
      // Fallback for gradient: use the first stop as the solid color representation
      const first = payload.gradient?.stops?.[0]?.hexa || '#000000FF';
      color = ColorHelper(first);
    }
    const rgba: RGBA = { r: color.red(), g: color.green(), b: color.blue(), a: color.alpha() };
    return rgba;
  };

  // Update the color of the color swatch on changes from the color picker only.
  const handlePickerChange = (payload: ColorPickerChangePayload) => {
    try {
      const color =
        payload.mode === 'gradient' ? payload.gradient : getSolidColorFromPayload(payload);
      if (color) {
        setInitialColor(color);
      }
    } catch {
      // ignore
    }
  };

  // Update the color of the color swatch and propagate change up to parent.
  const handlePickerChangeComplete = (payload: ColorPickerChangePayload) => {
    try {
      const color =
        payload.mode === 'gradient' ? payload.gradient : getSolidColorFromPayload(payload);
      if (color) {
        onChange?.(color);
        setInitialColor(color);
      }
    } catch {
      // ignore
    }
  };

  const handleClosePicker = () => {
    setShowPicker(false);
    const w = window as unknown as { __lpColorPickerOpen?: number };
    if (typeof w.__lpColorPickerOpen === 'number') {
      w.__lpColorPickerOpen = Math.max(0, (w.__lpColorPickerOpen ?? 1) - 1);
    }
  };

  // Keep name in sync with label prop
  useEffect(() => {
    setName(label || 'Color');
  }, [label]);

  // Keep isRenaming ref in sync for outside click handler
  useEffect(() => {
    isRenamingRef.current = isRenaming;
  }, [isRenaming]);

  // Focus input when entering rename mode
  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  // Outside click to finish rename
  useEffect(() => {
    const handlePointerDown = (e: Event) => {
      if (!isRenamingRef.current) return;
      const container = renameContainerRef.current;
      const target = e.target as Node | null;
      if (!container || !target) return;
      if (!container.contains(target)) finishRename(true);
    };
    const opts: AddEventListenerOptions = { capture: true };
    document.addEventListener('pointerdown', handlePointerDown, opts);
    return () => document.removeEventListener('pointerdown', handlePointerDown, opts);
  }, []);

  // Close on outside interactions ONLY when picker is not detached yet
  useEffect(() => {
    if (!showPicker) return;
    const onPointerDownCapture = (e: Event) => {
      if (pickerDetached) return; // once detached, only close via ColorPicker's own close button

      const target = e.target as Node | null;
      const portalEl = draggableRef.current;
      const containerEl = containerRef.current;
      if (!target) return;

      // Ignore if the click is inside any dropdown menu portal, color picker portal or
      // color style row container.
      const isInPicker = portalEl && portalEl.contains(target);
      const isInColorStyle = containerEl && containerEl.contains(target);
      const isInDropdown =
        target instanceof Element && target.closest('[data-radix-popper-content-wrapper]');
      if (isInPicker || isInColorStyle || isInDropdown) return;

      // Otherwise, close and adjust global flag
      setShowPicker(false);
      const w = window as unknown as { __lpColorPickerOpen?: number };
      if (typeof w.__lpColorPickerOpen === 'number') {
        w.__lpColorPickerOpen = Math.max(0, (w.__lpColorPickerOpen ?? 1) - 1);
      }
    };
    document.addEventListener('pointerdown', onPointerDownCapture, true);
    return () => document.removeEventListener('pointerdown', onPointerDownCapture, true);
  }, [showPicker, pickerDetached]);

  // Close when other style inspector dropdowns open (but only if not detached)
  useEffect(() => {
    const onStylesEditOpen = (e: Event) => {
      if (pickerDetached) return; // detached pickers are sticky, close only via close button
      const ev = e as CustomEvent<{ id?: string }>;
      // If another inspector opened, close this picker
      if (ev.detail?.id !== instanceId) {
        setShowPicker(false);
        const w = window as unknown as { __lpColorPickerOpen?: number };
        if (typeof w.__lpColorPickerOpen === 'number') {
          w.__lpColorPickerOpen = Math.max(0, (w.__lpColorPickerOpen ?? 1) - 1);
        }
      }
    };
    window.addEventListener('lp:styles-edit-open', onStylesEditOpen as EventListener);
    return () =>
      window.removeEventListener('lp:styles-edit-open', onStylesEditOpen as EventListener);
  }, [pickerDetached, instanceId]);

  // Listen for other Color pickers opening and close this one if it's not the opener
  useEffect(() => {
    const onOtherOpen = (e: Event) => {
      const ev = e as CustomEvent<{ id?: string }>;
      if (!ev.detail) return;
      // Once detached, ignore external close requests (scroll close, other pickers, etc.)
      if (pickerDetached) return;
      if (ev.detail.id !== instanceId) {
        setShowPicker(false);
      }
    };
    window.addEventListener('lp:colorpicker-open', onOtherOpen as EventListener);
    return () => {
      window.removeEventListener('lp:colorpicker-open', onOtherOpen as EventListener);
    };
  }, [instanceId, pickerDetached]);

  return (
    <div
      ref={containerRef}
      onClick={handleClick}
      className={clsx('inline-flex h-8 w-56 items-center justify-between px-2', className)}
    >
      {/* Left label */}
      {isRenaming ? (
        <div
          ref={(el) => {
            renameContainerRef.current = el;
          }}
          className="inline-flex h-8 max-h-8 min-h-8 w-36 items-center rounded-md border border-indigo-500 bg-neutral-50 px-2"
        >
          <input
            ref={inputRef}
            className="h-full w-full truncate border-none bg-transparent text-xs leading-none font-medium text-neutral-900 outline-none"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onBlur={() => finishRename(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') finishRename(true);
              if (e.key === 'Escape') finishRename(false);
            }}
          />
        </div>
      ) : (
        <div className="text-xs leading-none font-medium text-neutral-500">{name}</div>
      )}

      {/* Right controls */}
      <div className="flex items-center justify-start gap-1">
        {/* 28px icon button with dropdown */}
        <DropdownMenu
          onOpenChange={(next) => {
            const w = window as unknown as { __lpColorPickerOpen?: number };
            const pickerOpen = (w.__lpColorPickerOpen ?? 0) > 0;
            if (!next && pickerOpen) return;
          }}
        >
          <DropdownMenuTrigger asChild>
            <span onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
              <IconSimpleButton size={28} icon={IconMoreVertical} state="default" />
            </span>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className={clsx(
              'bg-neutral-0 flex w-28 flex-col items-start overflow-hidden rounded-md p-1 shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)] outline outline-offset-[-1px] outline-neutral-100',
            )}
            sideOffset={6}
            side="bottom"
            align="start"
          >
            <DropdownMenuItem
              className="inline-flex h-8 w-full cursor-pointer items-center justify-start rounded-lg px-2 py-2"
              onSelect={() => {
                handleRenameSelect();
              }}
            >
              <div className="text-xs leading-none font-medium text-neutral-900">Rename</div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Color swatch */}
        <div
          ref={swatchRef}
          className="flex h-7 w-7 cursor-pointer items-center justify-center gap-1 rounded-md bg-neutral-50 p-1"
          data-testid="color-swatch"
          onClick={(e) => {
            e.stopPropagation();
            openPicker();
          }}
        >
          {isSolid && (
            <div
              className={clsx('h-4 w-4 rounded border border-neutral-200')}
              style={{
                backgroundColor: '#' + rgbaToHexa(initialColor as RGBA),
              }}
            />
          )}
          {isGradient && (
            <div
              className={clsx('h-4 w-4 rounded border border-neutral-200')}
              style={{
                background: getGradientString(initialColor as Gradient),
              }}
            />
          )}
        </div>

        {showPicker &&
          createPortal(
            <Draggable
              handle=".colorpicker-drag-handle"
              nodeRef={draggableRef}
              defaultPosition={{ x: 0, y: 0 }}
              onStart={() => {
                setPickerDetached(true);
              }}
            >
              <div
                ref={draggableRef}
                data-colorpicker-portal
                data-detached={pickerDetached ? 'true' : 'false'}
                style={{
                  position: 'fixed',
                  top: pickerPos.top,
                  left: pickerPos.left,
                  zIndex: 100010,
                  maxHeight: 'calc(100vh - 16px)',
                  overflow: 'auto',
                }}
              >
                <ColorPicker
                  hasGradient={true}
                  title="Color Picker"
                  mode={isGradient ? 'gradient' : 'solid'}
                  hex={isSolid ? rgbaToHexa(initialColor as RGBA).slice(0, 6) : '000000'}
                  gradient={isGradient ? (initialColor as Gradient) : undefined}
                  opacity={isSolid ? Math.round(((initialColor as RGBA).a ?? 1) * 100) : 100}
                  onDetachAutoScroll={() => setPickerDetached(true)}
                  onChange={handlePickerChange}
                  onChangeComplete={handlePickerChangeComplete}
                  onClose={handleClosePicker}
                />
              </div>
            </Draggable>,
            document.body,
          )}
      </div>
    </div>
  );
};
